// S-21 — the ⌘K command-palette registry, extracted from ui-shell.tsx into a
// PURE DATA module (like placeholder-routes.ts / breadcrumb-map.ts) so the
// nav-coherence vitest can cross-check every palette route against the
// registered ROUTE_PATHS without importing the heavy app shell. ui-shell.tsx
// imports this — single source of truth, zero behavior change.
import { isRetiredRoute } from "../config/adapt-flags";

export interface Command {
  id: string;
  label: string;
  icon: string;
  route?: string;
  action?: "theme-light" | "theme-dark";
  hint?: string;
}

const ALL_COMMANDS: Command[] = [
  { id: "home", label: "خانه", icon: "home", route: "home", hint: "صفحه اصلی" },
  { id: "dashboard", label: "میز کار", icon: "home", route: "dashboard" },
  { id: "classroom", label: "کلاس زنده", icon: "live", route: "classroom" },
  { id: "course", label: "درس من", icon: "book", route: "course" },
  { id: "calendar", label: "تقویم", icon: "calendar", route: "calendar" },
  { id: "library", label: "کتابخانه", icon: "folder", route: "library" },
  { id: "labs", label: "آزمایشگاه‌های مجازی", icon: "flask", route: "labs" },
  { id: "recordings", label: "آرشیو ضبط‌ها", icon: "video", route: "recordings" },
  { id: "search", label: "جستجوی معنایی", icon: "search", route: "search" },
  { id: "community", label: "جامعه", icon: "users", route: "community" },
  { id: "messages", label: "پیام‌ها", icon: "chat", route: "messages" },
  { id: "inbox", label: "اعلان‌ها", icon: "bell", route: "inbox" },
  { id: "officehours", label: "Office Hours", icon: "headset", route: "officehours" },
  { id: "assessment", label: "آزمون‌ها و ارزشیابی", icon: "chip", route: "assessment" },
  { id: "submission", label: "تحویل تمرین", icon: "check", route: "submission" },
  { id: "transcript", label: "شناسنامهٔ آموزشی", icon: "file", route: "transcript" },
  { id: "degree-audit", label: "مسیر شایستگی/رتبه", icon: "target", route: "degree-audit" },
  { id: "registration", label: "نامزدسازی سازمانی", icon: "plus", route: "registration" },
  { id: "career", label: "مسیر ارتقای شغلی من", icon: "bolt", route: "career" },
  { id: "financial-aid", label: "کمک‌هزینه و بورسیه", icon: "dollar", route: "financial-aid" },
  { id: "wellness", label: "سلامت و مشاوره", icon: "shield", route: "wellness" },
  { id: "credential", label: "گواهی دیجیتال", icon: "cert", route: "credential" },
  { id: "alumni", label: "شبکه فارغ‌التحصیلان", icon: "grad", route: "alumni" },
  { id: "hackathons", label: "هکاتون‌ها و رقابت", icon: "trophy", route: "hackathons" },
  { id: "honor-code", label: "منشور صداقت علمی", icon: "shield", route: "honor-code" },
  { id: "schools", label: "دانشکده‌ها", icon: "grad", route: "schools" },
  { id: "faculty", label: "هیات علمی", icon: "users", route: "faculty" },
  { id: "research", label: "پژوهش", icon: "flask", route: "research" },
  { id: "events", label: "رویدادها", icon: "live", route: "events" },
  { id: "pricing", label: "پلن‌ها و قیمت", icon: "dollar", route: "pricing" },
  { id: "help", label: "پشتیبانی", icon: "headset", route: "help" },
  { id: "about", label: "درباره ما", icon: "globe", route: "about" },
  { id: "instructor", label: "کنسول استاد", icon: "grad", route: "instructor" },
  { id: "authoring", label: "تولید / ویرایش درس", icon: "sparkle", route: "admin/course-content" },
  { id: "analytics", label: "تحلیل‌گری نهادی", icon: "chart", route: "analytics" },
  { id: "admin", label: "میز مدیریت", icon: "settings", route: "admin" },
  { id: "regulation", label: "آیین‌نامه آموزشی", icon: "shield", route: "admin/regulation" },
  { id: "parent", label: "میز والد", icon: "users", route: "parent" },
  { id: "profile", label: "پروفایل عمومی", icon: "user", route: "profile" },
  { id: "bookmarks", label: "ذخیره‌ها", icon: "star", route: "bookmarks" },
  { id: "achievements", label: "دستاوردها", icon: "trophy", route: "achievements" },
  { id: "settings", label: "تنظیمات", icon: "settings", route: "settings" },
  // Primary public funnel: the REAL application form. Listed before the
  // /admissions demo tour so a search for «پذیرش» surfaces the real intake first.
  { id: "apply", label: "درخواست پذیرش", icon: "shield", route: "apply", hint: "فرم واقعی پذیرش" },
  { id: "track", label: "پیگیری درخواست", icon: "search", route: "track", hint: "وضعیت درخواست با کد پیگیری" },
  // (the old /admissions demo command was dropped — that route now hard-redirects to /apply,
  //  so a separate "demo" entry would just be a mislabelled duplicate of «درخواست پذیرش».)
  { id: "login", label: "ورود", icon: "user", route: "login" },
  // Theme commands removed (D-2026-06-13): dark mode disabled for now.
];

// S-NRI-ADAPT — دستورهای مربوط به ماژول‌های بازنشسته (مثل financial-aid) از پالت
// ⌘K حذف می‌شوند؛ برگشت‌پذیر با برداشتن از RETIRED_MODULES. تست nav-coherence
// (route ثبت‌شده) همچنان سبز است چون خودِ routeها حذف نمی‌شوند.
export const COMMANDS: Command[] = ALL_COMMANDS.filter((c) => !isRetiredRoute(c.route));
